import React from 'react'
import PropTypes from 'prop-types'
import {
  Card,
  CardContent,
  Typography,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core'
import ActionsMenu from 'components/actions-menu'
import MoreVertIcon from 'components/material-symbol/icons/MoreVert'
import EditOutlined from 'components/material-symbol/icons/Edit'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import GroupIcon from 'components/material-symbol/icons/Group'
import LockIcon from 'components/material-symbol/icons/Lock'
import EventOutlinedIcon from 'components/material-symbol/icons/Event'
import BookingTypeIconBadge from 'pages/pga-coach/bookings/booking-types/booking-type-icon-badge'
import { DateTime } from 'luxon'
import { useNoteCardStyles } from './styles'

function formatNoteDate(isoString) {
  if (!isoString) return ''
  const dt = DateTime.fromISO(isoString)
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : isoString
}

function getSessionContext(note) {
  if (note.notable?.__typename !== 'Session') return null
  const { title, startDateTime } = note.notable
  const dateLabel = startDateTime ? formatNoteDate(startDateTime) : null
  return {
    title: title || 'Session',
    dateLabel,
  }
}

function NoteCard({ note, onEdit, onDelete, onClick }) {
  const classes = useNoteCardStyles()
  const title = note.title || null
  const contentPreview = (note.content || '').trim()
  const isShared = !note.private
  const sessionContext = getSessionContext(note)
  const displayTitle = title || (sessionContext ? sessionContext.title : null)
  const privacyLabel = isShared ? 'Shared with your student' : 'Private to you'
  const isClickable = Boolean(onClick)

  const handleActivate = () => {
    onClick?.()
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleActivate()
    }
  }

  const ActivateRoot = isClickable ? 'button' : 'div'

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent className={classes.cardContent}>
        <div className={classes.titleRow}>
          <ActivateRoot
            type={isClickable ? 'button' : undefined}
            className={classes.activateArea}
            onClick={isClickable ? handleActivate : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
            aria-label={isClickable ? 'Edit note' : undefined}
          >
            {displayTitle ? (
              <Typography
                className={classes.title}
                variant="subtitle1"
                component="span"
              >
                {displayTitle}
              </Typography>
            ) : (
              <Typography
                className={classes.title}
                variant="subtitle1"
                component="span"
                color="textSecondary"
              >
                (No title)
              </Typography>
            )}
            <Typography
              className={classes.contentPreview}
              variant="body2"
              component="span"
            >
              {contentPreview || '—'}
            </Typography>
          </ActivateRoot>
          <div className={classes.menuWrap}>
            <ActionsMenu
              ariaId={`note-${note.id}`}
              aria-label="Note actions"
              buttonContent={<MoreVertIcon />}
              className={classes.menuButton}
              color="default"
              size="small"
            >
              <MenuItem onClick={onEdit}>
                <ListItemIcon className={classes.menuIcon}>
                  <EditOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Edit" />
              </MenuItem>
              <MenuItem onClick={onDelete}>
                <ListItemIcon className={classes.menuIcon}>
                  <DeleteOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Delete" />
              </MenuItem>
            </ActionsMenu>
          </div>
        </div>
        <div className={classes.footer}>
          <Typography className={classes.date} variant="caption">
            {formatNoteDate(note.updatedAt || note.createdAt)}
          </Typography>
          <div className={classes.chipRow}>
            {sessionContext ? (
              <BookingTypeIconBadge
                label={
                  sessionContext.dateLabel
                    ? `Session · ${sessionContext.dateLabel}`
                    : 'Session note'
                }
                variant="accessCode"
              >
                <EventOutlinedIcon />
              </BookingTypeIconBadge>
            ) : null}
            <BookingTypeIconBadge label={privacyLabel} variant="private">
              {isShared ? <GroupIcon /> : <LockIcon />}
            </BookingTypeIconBadge>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    private: PropTypes.bool,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    notable: PropTypes.shape({
      __typename: PropTypes.string,
      title: PropTypes.string,
      startDateTime: PropTypes.string,
    }),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClick: PropTypes.func,
}

NoteCard.defaultProps = {
  onClick: null,
}

export default NoteCard
